import React, { useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import CustomInput from '../UI/CustomInput/CustomInput'
import { measurementUnits } from '../../constants/constants'
import iconEdit from '../../assets/icons/iconEdit.svg'
import IconTrashRed from '../../assets/icons/IconTrashRed.svg'
import { Formik, Form, FormikHelpers, FormikValues, FormikProps } from 'formik'
import * as yup from 'yup'
import close from '../../assets/icons/close.svg'
import styles from './CreateProductModal.module.css'
import CustomButton from '../UI/CustomButton/CustomButton'
import s from '../../styles/common/Form.module.css'
import { CategoriesType, ProductsType } from '../../redux/actions/types'
import UploadPhotoComponent from '../UploadPhotoComponent/UploadPhotoComponent'
import { UploadImagesType } from '../../types'

import exit from '../../assets/icons/trash.svg'
import {
  changeProductMainInformation,
  createProduct,
  deleteCurrentFileFromProduct,
  deleteCurrentProduct,
  getCurrentProduct,
  pushNewFilesToProduct,
} from '../../redux/actions/productsActon'
import Select from 'react-select'
import CustomModal from '../UI/CustomModal/CustomModal'
import DeleteComponent from '../DeleteComponent/DeleteComponent'
import { useToast } from '../../context/ToastContext'
import { getCurrentCategory } from '../../redux/actions/categoriesAction'
import CustomTextArea from '../UI/CustomTextArea/CustomTextArea'

interface CreateProductModalProps {
  currentCat?: CategoriesType
  isEditMode: boolean
  isReadOnlyMode?: boolean // New prop for read-only mode
  isOpen: boolean
  onClose: () => void
  currentProduct?: ProductsType
  menuId: string
}

const CreateProductModal: React.FC<CreateProductModalProps> = ({
  isOpen,
  onClose,
  currentCat,
  isEditMode,
  isReadOnlyMode = false,
  menuId,
}) => {
  const formikRef = useRef<FormikProps<any>>(null)
  const dispatch = useAppDispatch()
  const { category, currentCategory } = useAppSelector((state) => state.categories)
  const [isInEditMode, setIsInEditMode] = useState(isEditMode)

  const { userRole, token } = useAppSelector((state) => state.auth)

  const [loaded, setLoaded] = useState<boolean>(false)
  const [visibleDelete, setVisibleDelete] = useState<boolean>(false)
  const [photos, setPhotos] = useState<UploadImagesType[]>([])
  const [photosFile, setPhotosFile] = useState<any[]>([])

  const { showSuccess, showError } = useToast()
  const { error, currentProduct } = useAppSelector((state) => state.products)

  const validationSchema = yup.object().shape({
    name: yup.string().required('Название продукта обязательно'),
    price: yup.number().typeError('Цена должна быть числом').required('Цена обязательна'),
    measurement: yup.string().required('Единица измерения обязательна'),
    weight: yup.string().required('Масса обязательна'),
  })

  const handleSubmitHandler = async (values: FormikValues) => {
    const { name, description, price, measurement, weight } = values

    const formData = new FormData()

    const maxSizeInBytes = 20 * 1024 * 1024

    for (let i = 0; i < photosFile.length; i++) {
      const sizeInBytes = photosFile[i].size

      if (sizeInBytes >= maxSizeInBytes) {
        showError('Одна или несколько картинок слишком большие. Максимальный размер: 20 МБ.')

        return
      }
    }

    if (photosFile.length > 10) {
      showError('Вы можете загрузить максимум 10 файлов или картинок')
      return
    }

    if (photosFile.length > 0) {
      for (let i = 0; i < photosFile.length; i++) {
        formData.append('images', photosFile[i])
      }
    }

    formData.append('name', name)
    formData.append('description', description)
    formData.append('price', String(price))
    formData.append('measurement', measurement)
    formData.append('weight', String(weight))
    formData.append('quantity', String(1))
    formData.append('categoryId', String(currentCat?.id))

    setLoaded(true)
    const resultAction = await dispatch(createProduct({ formData, token }))

    if (createProduct.fulfilled.match(resultAction)) {
      showSuccess('Продукт успешно создан')
      setLoaded(false)
      dispatch(getCurrentCategory({ categoryId: String(currentCategory.id), token }))
      onClose()
    } else {
      showError('Произошла ошибка при создании продукта')
      setLoaded(false)
    }
  }

  const handleSubmitHandlerEdit = async (
    values: FormikValues,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<any>,
  ) => {
    const { name, description, price, measurement, weight, images } = values

    const maxSizeInBytes = 20 * 1024 * 1024

    for (let i = 0; i < photosFile.length; i++) {
      const sizeInBytes = photosFile[i].size

      if (sizeInBytes >= maxSizeInBytes) {
        showError('Одна или несколько картинок слишком большие. Максимальный размер: 20 МБ.')

        return
      }
    }
    if (photosFile.length > 10) {
      showError('Вы можете загрузить максимум 10 файлов или картинок')

      return
    }

    const updatedImages = [...photos, ...photosFile]

    const requestData = {
      name,
      description,
      price: String(price),
      measurement,
      weight: String(weight),
      quantity: String(1),
      productId: String(currentProduct?.id),
    }

    setLoaded(true)
    const resultAction = await dispatch(
      changeProductMainInformation({
        token,
        name: requestData.name,
        description: requestData.description,
        price: requestData.price,
        measurement: requestData.measurement,
        weight: requestData.weight,
        quantity: requestData.quantity,
        productId: requestData.productId,
      }),
    )

    await onUploadNewImages()

    if (changeProductMainInformation.fulfilled.match(resultAction)) {
      showSuccess('Продукт успешно изменен')
      setLoaded(false)

      dispatch(getCurrentCategory({ categoryId: String(currentCategory.id), token }))
      setIsInEditMode(false)
    } else {
      showError('Произошла ошибка при изменении продукта')
      setLoaded(false)
    }

    setSubmitting(false)
  }

  if (!isOpen) return null

  const onDeletePlatformHandler = async () => {
    const resultAction = await dispatch(
      deleteCurrentProduct({ productId: String(currentProduct.id), token }),
    )
    if (deleteCurrentProduct.rejected.match(resultAction)) {
      showError('Произошла ошибка при удалении продукта')
      setIsInEditMode(false)
    } else {
      showSuccess('Продукт успешно удален')
      onClose()
      dispatch(getCurrentCategory({ categoryId: String(currentCategory.id), token }))
    }
  }

  const onUploadNewImages = async () => {
    const formData = new FormData()

    if (photosFile.length > 10) {
      showError('Вы можете загрузить максимум 10 картинок')

      return
    }

    if (photosFile.length !== 0) {
      for (let i = 0; i < photosFile.length; i++) {
        formData.append('images', photosFile[i])
      }

      const resultAction = await dispatch(
        pushNewFilesToProduct({ productId: String(currentProduct.id), formData, token }),
      )

      if (pushNewFilesToProduct.rejected.match(resultAction)) {
        showError('Произошла ошибка при загрузке файлов')
      } else {
        showSuccess('Файлы успешно загружен')
        setPhotosFile([])
        setPhotos([])
        await dispatch(getCurrentProduct({ productId: String(currentProduct.id), token }))
      }
    }
  }

  const onDeleteImageFromServer = async (image: string) => {
    const resultAction = await dispatch(
      deleteCurrentFileFromProduct({
        token,
        productId: String(currentProduct.id),
        filesToDelete: [image],
      }),
    )

    if (deleteCurrentFileFromProduct.rejected.match(resultAction)) {
      showError('Произошла ошибка при удалении файла')
    } else {
      showSuccess('Файл успешно удален')
      await dispatch(getCurrentProduct({ productId: String(currentProduct.id), token }))
    }
  }

  const handleEditClick = () => {
    setIsInEditMode(true)
  }

  return (
    <div className={styles.createMenuModalOverlay}>
      <CustomModal visible={visibleDelete} setVisible={setVisibleDelete}>
        <DeleteComponent
          title='Удаление продукта'
          description='Хотите удалить продукт'
          onCancelHandler={() => setVisibleDelete(false)}
          onDeleteHandler={onDeletePlatformHandler}
        />
      </CustomModal>
      <div className={styles.createMenuModal}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>
            {isInEditMode
              ? 'Изменить продукт'
              : isReadOnlyMode
              ? 'Карточка продукта'
              : 'Создать продукт'}
          </h1>
          <img
            onClick={() => {
              formikRef.current?.resetForm()
              setIsInEditMode(false)
              onClose()
            }}
            className={styles.close}
            src={close}
            alt='close'
          />
        </div>

        <Formik
          innerRef={formikRef}
          initialValues={{
            name:
              (isInEditMode || isReadOnlyMode) && currentProduct?.name ? currentProduct?.name : '',
            description:
              (isInEditMode || isReadOnlyMode) && (currentProduct as ProductsType)?.description
                ? (currentProduct as ProductsType)?.description
                : '',
            images:
              (isInEditMode || isReadOnlyMode) && currentProduct.images
                ? currentProduct.images
                : [],
            price:
              (isInEditMode || isReadOnlyMode) && (currentProduct as ProductsType)?.price
                ? (currentProduct as ProductsType)?.price
                : '',
            weight:
              (isInEditMode || isReadOnlyMode) && (currentProduct as ProductsType)?.weight
                ? (currentProduct as ProductsType)?.weight
                : '',
            measurement:
              (isInEditMode || isReadOnlyMode) && (currentProduct as ProductsType)?.measurement
                ? (currentProduct as ProductsType)?.measurement
                : '',
          }}
          validationSchema={validationSchema}
          onSubmit={isInEditMode ? handleSubmitHandlerEdit : handleSubmitHandler}
          enableReinitialize
        >
          {({ values, touched, setFieldValue, errors, handleChange, handleBlur }) => {
            const handleDeleteImage = (e: React.MouseEvent, index: number) => {
              const updatedPhotos = [...photos]
              const updatedPhotosFile = [...photosFile]
              updatedPhotos.splice(index, 1)
              updatedPhotosFile.splice(index, 1)
              setPhotos(updatedPhotos)
              setPhotosFile(updatedPhotosFile)
              setFieldValue('images', updatedPhotosFile)
            }

            return (
              <Form className={styles.inputBlocks}>
                <div className={styles.wrapperFields}>
                  {isReadOnlyMode && !isInEditMode ? (
                    currentProduct.images.length > 0 && (
                      <div className={styles.imageUpload}>
                        <div className={styles.readOnlyImagesContainer}>
                          {currentProduct.images.map((photo: any, index: number) => (
                            <div className={styles.imagesContainer} key={index}>
                              <img
                                className={styles.image}
                                src={`${process.env.REACT_APP_BASE_URL_IMAGES}${photo}`}
                                alt={'123'}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  ) : (
                    <div className={styles.imageUpload}>
                      <UploadPhotoComponent
                        editMode={isInEditMode}
                        photos={photos}
                        photosFile={photosFile}
                        setPhotos={setPhotos}
                        widthOneImageContainer={245}
                        widthOneGapOfImageContainer={20}
                        setPhotosFiles={setPhotosFile}
                      >
                        <>
                          {currentProduct.images.length > 0 &&
                            currentProduct.images.map((photo: any, index: number) => (
                              <div
                                className={styles.imagesContainer}
                                key={index}
                                data-imgindex={index}
                              >
                                {isInEditMode && (
                                  <div className={styles.imageExitContainer}>
                                    <img
                                      className={styles.imageExit}
                                      src={exit}
                                      alt='exit'
                                      onClick={() => onDeleteImageFromServer(photo)}
                                      data-imgindex={index}
                                    />
                                  </div>
                                )}
                                <img
                                  className={styles.image}
                                  src={`${process.env.REACT_APP_BASE_URL_IMAGES}${photo}`}
                                  alt={photo.name}
                                />
                              </div>
                            ))}
                          {photos.length > 0 &&
                            photos.map((photo, index) => (
                              <div
                                className={styles.imagesContainer}
                                key={index}
                                data-imgindex={index}
                              >
                                <div className={styles.imageExitContainer}>
                                  <img
                                    className={styles.imageExit}
                                    src={exit}
                                    alt='exit'
                                    onClick={(e) => handleDeleteImage(e, index)}
                                    data-imgindex={index}
                                  />
                                </div>
                                <img className={styles.image} src={photo?.src} alt={photo.name} />
                              </div>
                            ))}
                        </>
                      </UploadPhotoComponent>
                    </div>
                  )}

                  <div
                    style={isReadOnlyMode ? { flexDirection: 'row' } : {}}
                    className={styles.inputBlock}
                  >
                    <div className={styles.leftSide}>Название</div>
                    <div className={styles.rightSide}>
                      {isReadOnlyMode && !isInEditMode ? (
                        <div className={styles.readOnlyField}>{values.name}</div>
                      ) : (
                        <CustomInput
                          type='text'
                          placeholder='Введите'
                          name='name'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.name}
                          className={
                            errors.name && touched.name
                              ? `${s.inputError} ${styles.input}`
                              : styles.input
                          }
                          disabled={isReadOnlyMode && !isInEditMode}
                        />
                      )}
                      {touched.name && errors.name && (
                        <p className={s.errorValidation}>{errors.name as string}</p>
                      )}
                    </div>
                  </div>
                  <div
                    style={isReadOnlyMode ? { flexDirection: 'row' } : {}}
                    className={styles.inputBlock}
                  >
                    <div className={styles.leftSide}>Цена</div>
                    <div className={styles.rightSide}>
                      {isReadOnlyMode && !isInEditMode ? (
                        <div className={styles.readOnlyField}>{values.price}</div>
                      ) : (
                        <CustomInput
                          type='text'
                          placeholder='Введите'
                          name='price'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.price}
                          className={
                            errors.price && touched.price
                              ? `${s.inputError} ${styles.input}`
                              : styles.input
                          }
                          disabled={isReadOnlyMode && !isInEditMode}
                        />
                      )}
                      {touched.price && errors.price && (
                        <p className={s.errorValidation}>{errors.price as string}</p>
                      )}
                    </div>
                  </div>

                  <div
                    style={isReadOnlyMode ? { flexDirection: 'row' } : {}}
                    className={styles.inputBlock}
                  >
                    <div className={styles.leftSide}>Единица измерения</div>
                    <div className={styles.rightSide}>
                      {isReadOnlyMode && !isInEditMode ? (
                        <div className={styles.readOnlyField}>{values.measurement}</div>
                      ) : (
                        <Select
                          className={styles.reactSelect}
                          classNamePrefix='react-select'
                          placeholder='Единица измерения'
                          value={
                            values.measurement
                              ? { value: values.measurement, label: values.measurement }
                              : null
                          }
                          options={measurementUnits.map((unit) => ({
                            value: unit,
                            label: unit,
                          }))}
                          onChange={(selectedOption) => {
                            handleChange({
                              target: {
                                name: 'measurement',
                                value: selectedOption ? selectedOption.value : '',
                              },
                            })
                          }}
                          onBlur={handleBlur}
                          name='measurement'
                          required
                          isDisabled={isReadOnlyMode && !isInEditMode}
                          isClearable={false}
                          noOptionsMessage={() => 'Нет доступных вариантов'}
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              border: 'none',
                              boxShadow: 'none',
                              minHeight: 'auto',
                              backgroundColor: state.isDisabled ? 'transparent' : 'transparent',
                              height: 24,
                            }),
                            menu: (provided) => ({
                              ...provided,
                              zIndex: 5,
                              right: 0,
                            }),
                            input: (provided) => ({
                              ...provided,
                              margin: 0,
                              padding: 0,
                            }),
                            valueContainer: (provided) => ({
                              ...provided,
                              padding: 0,
                            }),
                            dropdownIndicator: (provided) => ({
                              ...provided,
                              padding: 0,
                              display: isReadOnlyMode && !isInEditMode ? 'none' : 'flex',
                            }),
                            clearIndicator: (provided) => ({
                              ...provided,
                              padding: 0,
                            }),
                            indicatorSeparator: () => ({
                              display: 'none',
                            }),
                            placeholder: (provided) => ({ ...provided, fontSize: 14 }),
                          }}
                        />
                      )}
                      {touched.measurement && errors.measurement && (
                        <p className={s.errorValidation}>{errors.measurement as string}</p>
                      )}
                    </div>
                  </div>
                  <div
                    style={isReadOnlyMode ? { flexDirection: 'row' } : {}}
                    className={styles.inputBlock}
                  >
                    <div className={styles.leftSide}>Масса</div>
                    <div className={styles.rightSide}>
                      {isReadOnlyMode && !isInEditMode ? (
                        <div className={styles.readOnlyField}>{values.weight}</div>
                      ) : (
                        <CustomInput
                          type='text'
                          placeholder='Введите'
                          name='weight'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.weight}
                          className={
                            errors.weight && touched.weight
                              ? `${s.inputError} ${styles.input}`
                              : styles.input
                          }
                          disabled={isReadOnlyMode && !isInEditMode}
                        />
                      )}
                      {touched.weight && errors.weight && (
                        <p className={s.errorValidation}>{errors.weight as string}</p>
                      )}
                    </div>
                  </div>

                  <div
                    style={isReadOnlyMode ? { flexDirection: 'row' } : {}}
                    className={styles.inputBlock}
                  >
                    <div className={styles.leftSide}>Описание</div>
                    <div className={styles.rightSide}>
                      {isReadOnlyMode && !isInEditMode ? (
                        <div className={styles.readOnlyField}>{values.description || '-'}</div>
                      ) : (
                        <CustomTextArea
                          className={`${styles.input} ${styles.textarea}`}
                          withoutFile
                          placeholder='Введите'
                          name='description'
                          onChange={handleChange}
                          disabled={isReadOnlyMode && !isInEditMode}
                          onBlur={handleBlur}
                          value={values.description}
                        ></CustomTextArea>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.footerContainer}>
                  {isInEditMode || isReadOnlyMode ? (
                    <CustomButton
                      onClick={() => setVisibleDelete(true)}
                      iconSrc={IconTrashRed}
                      className={styles.deleteBtn}
                      title={isReadOnlyMode ? '' : 'Удалить продукт'}
                    />
                  ) : isReadOnlyMode ? (
                    <div></div>
                  ) : (
                    <div></div>
                  )}
                  {isReadOnlyMode && !isInEditMode ? (
                    <CustomButton
                      iconSrc={iconEdit}
                      className={styles.editBtn}
                      title={''}
                      type='button'
                      onClick={(e) => {
                        e.preventDefault()
                        handleEditClick()
                      }}
                    />
                  ) : (
                    <CustomButton
                      className={styles.addSiteBtn}
                      title={isInEditMode ? 'Сохранить' : 'Создать'}
                      type='submit'
                    />
                  )}
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default CreateProductModal
